// Opportunity Stage is derived on the client from baselineWinChance via the stage mapping:
// 0% = Lost · 1–19 New Lead · 20–39 Discovery · 40–59 Qualified ·
// 60–89 Proposal · 90–99 Contract Sent · 100% = Won. 0% and 100% are DEDICATED Lost/Won stage
// values (not New Lead / Contract Sent). baselineWinChance is a fraction 0..1; it is non-null
// (DB default 1.00), so null/undefined coalesces to 1.00 ⇒ Won.

export type OpportunityStageKey =
	| 'LOST'
	| 'NEW_LEAD'
	| 'DISCOVERY'
	| 'QUALIFIED_OPPORTUNITY'
	| 'PROPOSAL_SENT'
	| 'CONTRACT_SENT'
	| 'WON';

export const OPPORTUNITY_STAGE_MESSAGE_IDS: Record<OpportunityStageKey, string> = {
	LOST: 'opportunities.stage.lost',
	NEW_LEAD: 'opportunities.stage.new_lead',
	DISCOVERY: 'opportunities.stage.discovery',
	QUALIFIED_OPPORTUNITY: 'opportunities.stage.qualified',
	PROPOSAL_SENT: 'opportunities.stage.proposal_sent',
	CONTRACT_SENT: 'opportunities.stage.contract_sent',
	WON: 'opportunities.stage.won',
};

export function deriveOpportunityStage(baselineWinChance: number | null | undefined): OpportunityStageKey {
	const pct = Math.round((baselineWinChance ?? 1) * 100);
	if (pct === 0) return 'LOST';
	if (pct <= 19) return 'NEW_LEAD';
	if (pct <= 39) return 'DISCOVERY';
	if (pct <= 59) return 'QUALIFIED_OPPORTUNITY';
	if (pct <= 89) return 'PROPOSAL_SENT';
	if (pct <= 99) return 'CONTRACT_SENT';
	return 'WON';
}

// The win-probability bands (label + colour) mirror the stage bands. The list's Win Probability column
// renders the exact % (tinted in the band colour), not the band range; these band labels stay available
// (e.g. winBandLabel) and the colours are the traffic-light hues from the design (red → orange → amber →
// blue → green) — tweak to design tokens if the palette shifts.
export interface WinProbabilityBand {
	label: string;
	color: string;
}

const WIN_PROBABILITY_BANDS: Record<OpportunityStageKey, WinProbabilityBand> = {
	LOST: {label: '0%', color: '#a1a1aa'},
	NEW_LEAD: {label: '1–19%', color: '#e5484d'},
	DISCOVERY: {label: '20–39%', color: '#f76808'},
	QUALIFIED_OPPORTUNITY: {label: '40–59%', color: '#f5a623'},
	PROPOSAL_SENT: {label: '60–89%', color: '#0091ff'},
	CONTRACT_SENT: {label: '90–99%', color: '#30a46c'},
	WON: {label: '100%', color: '#30a46c'},
};

export function winProbabilityBand(baselineWinChance: number | null | undefined): WinProbabilityBand {
	return WIN_PROBABILITY_BANDS[deriveOpportunityStage(baselineWinChance)];
}
